import axios from 'axios'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { keyBy } from 'lodash'

import { fetchIfNeededWrapper, fetchWrapper } from '@/reducers/fetch'
import { AnnualTaxDateMapping } from './annualTaxDateTypes.slice'

// Client-side models
export interface AnnualTaxDetail extends AnnualTaxDateMapping {
  taxYear: string
  status: AnnualTaxDetailStatuses
  createdAt: string
  updatedAt: string
  modifiedBy?: number | null
}

export enum AnnualTaxDetailStatuses {
  current = 'current',
  future = 'future',
  past = 'past',
}

export interface AnnualTaxDetailsState {
  [key: string]: AnnualTaxDetail
}

// Our slice object contains the reducer, which modifies state for this entity
const annualTaxDetailsSlice = createSlice({
  name: 'annualTaxDetails',
  initialState: {} as AnnualTaxDetailsState,
  reducers: {
    receiveSingleAnnualTaxDetail: (
      state,
      action: PayloadAction<AnnualTaxDetail>
    ) => {
      state[action.payload.taxYear] = action.payload
    },
    receiveAllAnnualTaxDetails: (
      state,
      action: PayloadAction<{
        [key: string]: AnnualTaxDetail
      }>
    ) => ({ ...state, ...action.payload }),
  },
})

// Exported so we can access within appReducer.ts
export default annualTaxDetailsSlice.reducer

// These functions are used to manipulate our Redux state
const { receiveSingleAnnualTaxDetail, receiveAllAnnualTaxDetails } =
  annualTaxDetailsSlice.actions

/*
  Remaining logic below provides REST functionality to our UI components
*/

// Keys used within our fetchWrapper. Sometimes used by UI components for ascertaining the request's state
export const FETCH_ALL_ANNUAL_TAX_DETAILS_KEY =
  'FETCH_ALL_ANNUAL_TAX_DETAILS_KEY'
export const UPDATE_SINGLE_ANNUAL_TAX_DETAIL_KEY =
  'UPDATE_SINGLE_ANNUAL_TAX_DETAIL_KEY'
export const CREATE_SINGLE_ANNUAL_TAX_DETAIL_KEY =
  'CREATE_SINGLE_ANNUAL_TAX_DETAIL_KEY'

// These functions are called by our UI components

// For entities whose data changes infrequently, we use the "IfNeeded()" pattern to prevent unnecessary requests
export const fetchAllAnnualTaxDetailsIfNeeded = () =>
  fetchIfNeededWrapper({
    fetchKey: FETCH_ALL_ANNUAL_TAX_DETAILS_KEY,
    defaultErrorMessage: 'Error fetching all annual tax details',
    fetchFunction: async (dispatch) => {
      // Make the GET request
      const json = await axios.get<AnnualTaxDetail[]>(
        '/finances/api/v1/annual_tax_details'
      )

      // Update Redux state with data
      dispatch(receiveAllAnnualTaxDetails(keyBy(json.data, 'taxYear')))
      return json.data
    },
  })

export const updateSingleAnnualTaxDetail = (
  taxYear: string,
  data: Pick<AnnualTaxDetail, 'modifiedBy'> & {
    taxDates: { id: string; value: string }[]
  }
) =>
  fetchWrapper({
    fetchKey: UPDATE_SINGLE_ANNUAL_TAX_DETAIL_KEY + taxYear,
    defaultErrorMessage: 'Error updating the annual tax detail',
    fetchFunction: async (dispatch) => {
      const url = `/finances/api/v1/admin/annual_tax_details/${taxYear}`
      const json = await axios.put<AnnualTaxDetail>(url, data)
      dispatch(receiveSingleAnnualTaxDetail(json.data))
      return json.data
    },
  })

export const createSingleAnnualTaxDetail = (
  data: Pick<AnnualTaxDetail, 'modifiedBy' | 'taxYear'> & {
    taxDates: { id: string; value: string }[]
  }
) =>
  fetchWrapper({
    fetchKey: CREATE_SINGLE_ANNUAL_TAX_DETAIL_KEY,
    defaultErrorMessage: 'Error creating the annual tax detail',
    fetchFunction: async (dispatch) => {
      const url = '/finances/api/v1/admin/annual_tax_details'
      const json = await axios.post<AnnualTaxDetail>(url, data)
      dispatch(receiveSingleAnnualTaxDetail(json.data))
      return json.data
    },
  })
